import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header, SectionLabel } from "../components/Brand";
import { api, formatIST, BACKEND_URL } from "../lib/api";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";

export default function SuccessPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => {
    api
      .get(`/candidate/${candidateId}`)
      .then((r) => {
        const cand = r.data.candidate;
        if (cand.submission_status !== "submitted") {
          navigate(`/assignment/${candidateId}`);
        } else {
          setC(cand);
        }
      })
      .catch(() => navigate("/start"));
  }, [candidateId, navigate]);

  if (!c) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  const sub = c.submission || {};
  const Row = ({ label, value, link }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-6 py-3 border-b border-white/5">
      <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="sm:col-span-2 text-sm text-zinc-200 break-all">
        {link && value ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="text-white hover:underline inline-flex items-center gap-1"
          >
            {value}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : (
          value || "—"
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="success-page">
      <Header />
      <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24">
        <div className="border border-[var(--bh-accent-border)] bg-[var(--bh-accent-soft)] rounded-sm p-8 mb-10 flex items-start gap-5">
          <div className="h-12 w-12 rounded-full bg-[var(--bh-accent-soft)] border border-[var(--bh-accent-border)] flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 text-[#ff4950]" />
          </div>
          <div>
            <SectionLabel className="mb-2">Submission received</SectionLabel>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3 text-white">
              Submission received successfully.
            </h1>
            <p className="text-zinc-200 text-sm leading-relaxed max-w-2xl">
              Thank you for completing the Brandhero Buyer Conviction Sprint.
              Shortlisted candidates will be contacted for the next interaction round.
              You can close this tab.
            </p>
          </div>
        </div>

        <section className="border border-white/10 rounded-sm p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <SectionLabel>Your submitted answers</SectionLabel>
            <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
              {c.candidate_code}
            </div>
          </div>
          <Row label="Full name" value={sub.full_name || c.full_name} />
          <Row label="Email" value={sub.email || c.email} />
          <Row label="Phone" value={sub.phone || c.phone} />
          <Row label="Branch" value={sub.branch || c.branch} />
          <Row label="Year" value={sub.year || c.year} />
          <Row label="LinkedIn" value={sub.linkedin_url} link />
          <Row label="Portfolio / GitHub" value={sub.portfolio_url} link />
          <Row label="Google Docs" value={sub.docs_url} link />
          <Row label="Google Sheet" value={sub.sheet_url} link />
          <Row label="Walkthrough / Explanation Video" value={sub.walkthrough_url} link />
          <Row label="AI / tools used" value={sub.ai_tools_used} />
          <Row label="Time spent" value={sub.time_spent} />
          <Row
            label="CV attached"
            value={
              sub.cv_attached === "yes" && sub.cv_filename
                ? sub.cv_filename
                : sub.cv_attached === "no"
                ? "Will email separately"
                : "—"
            }
          />
          {sub.cv_attached === "yes" && sub.cv_file_path && (
            <Row
              label="CV file"
              value={`${BACKEND_URL}/api/files/download?path=${encodeURIComponent(sub.cv_file_path)}`}
              link
            />
          )}
          <Row label="Started at (IST)" value={formatIST(c.start_at)} />
          <Row label="Submitted at (IST)" value={formatIST(c.submitted_at)} />
        </section>
      </main>
    </div>
  );
}
